import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FixedNav, Caption, Card, Button, StatStrip, AnimatedNumber, PulseDot } from '@/ui';
import { practiceApi } from './api';
import { CADENCE_TIMING } from './types';
import type { PracticeGameSummary } from './types';

/**
 * Practice Arena — page d'accueil du module (Salve P1 T3).
 *
 * Liste les 20 dernières parties de Léon + bouton "🎯 Nouvelle partie" qui
 * navigate vers /practice-arena/setup. Header avec PulseDot orange et stats
 * agrégées (compteur total, win rate, ELO interne estimé).
 *
 * Wording :
 *   - Tagline : « Joue, perds, apprends. »
 *   - Couleur module : orange (DESIGN extension P1, distinct amber/coral).
 */

const RESULT_LABEL_FR = {
  win: 'Victoire',
  loss: 'Défaite',
  draw: 'Nulle',
  abandoned: 'Abandon',
} as const;

const RESULT_COLOR = {
  win: 'text-volt-500',
  loss: 'text-coral-500',
  draw: 'text-ink-300',
  abandoned: 'text-amber-500',
} as const;

export function PracticeArenaListPage(): JSX.Element {
  const navigate = useNavigate();

  const listQ = useQuery({
    queryKey: ['practice', 'list'],
    queryFn: () => practiceApi.listGames({ limit: 20 }),
  });

  const stats = useMemo(() => {
    const games = listQ.data?.games ?? [];
    const finished = games.filter((g) => g.result !== null && g.result !== 'abandoned');
    const wins = finished.filter((g) => g.result === 'win').length;
    const winRate = finished.length > 0 ? Math.round((wins / finished.length) * 100) : 0;
    return {
      total: games.length,
      finished: finished.length,
      winRate,
    };
  }, [listQ.data]);

  return (
    <div className="min-h-screen bg-ink-900 text-paper-50">
      <FixedNav />
      <main className="pt-20 pb-12 px-4 md:px-8 max-w-6xl mx-auto">
        {/* Header module */}
        <div className="flex items-center gap-3 mb-2">
          <PulseDot color="var(--orange-500)" />
          <Caption color="orange">Practice Arena</Caption>
        </div>
        <h1 className="text-heading-1 font-display font-semibold mb-1">L'Arène</h1>
        <p className="text-ink-300 text-body-lg italic mb-8">
          Joue, perds, apprends.
        </p>

        {/* Stats agrégées */}
        <StatStrip className="mb-8">
          <div>
            <Caption>Parties</Caption>
            <div className="text-display-2 font-display tabular-nums">
              <AnimatedNumber value={stats.total} />
            </div>
          </div>
          <div>
            <Caption>Terminées</Caption>
            <div className="text-display-2 font-display tabular-nums">
              <AnimatedNumber value={stats.finished} />
            </div>
          </div>
          <div>
            <Caption>Taux victoire</Caption>
            <div className="text-display-2 font-display tabular-nums">
              <AnimatedNumber value={stats.winRate} format={(v) => `${Math.round(v)}%`} />
            </div>
          </div>
        </StatStrip>

        {/* Bouton "Nouvelle partie" */}
        <div className="mb-8">
          <Button
            variant="primary"
            accent="orange"
            onClick={() => navigate('/practice-arena/setup')}
            data-testid="new-game-btn"
          >
            🎯 Nouvelle partie classique
          </Button>
        </div>

        {/* Liste des parties */}
        <Card accent="orange" padding="default">
          <div className="flex items-center justify-between mb-4">
            <Caption color="orange">Tes dernières parties</Caption>
          </div>
          {listQ.isLoading && (
            <p className="text-ink-300 text-body-sm" data-testid="loading">
              Chargement…
            </p>
          )}
          {listQ.isError && (
            <p className="text-coral-500 text-body-sm" data-testid="error">
              Erreur de chargement.
            </p>
          )}
          {listQ.data && listQ.data.games.length === 0 && (
            <p className="text-ink-300 text-body italic" data-testid="empty">
              Aucune partie pour l'instant. Lance ta première arène.
            </p>
          )}
          {listQ.data && listQ.data.games.length > 0 && (
            <ul className="space-y-2" data-testid="game-list">
              {listQ.data.games.map((g) => (
                <PracticeGameRow key={g.id} game={g} onOpen={() => navigate(`/practice-arena/review/${g.id}`)} />
              ))}
            </ul>
          )}
        </Card>
      </main>
    </div>
  );
}

function PracticeGameRow({
  game,
  onOpen,
}: {
  game: PracticeGameSummary;
  onOpen: () => void;
}): JSX.Element {
  const date = new Date(game.started_at);
  const dateLabel = date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  const cadenceLabel = CADENCE_TIMING[game.cadence].label;
  const colorLabel = { white: 'Blancs', black: 'Noirs', random: 'Aléatoire' }[game.color];
  const resultLabel = game.result ? RESULT_LABEL_FR[game.result] : 'En cours';
  const resultColor = game.result ? RESULT_COLOR[game.result] : 'text-ink-300';

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="w-full flex items-center justify-between gap-4 py-2 px-3 rounded-lg hover:bg-ink-700 transition-colors text-left"
        data-testid={`game-row-${game.id}`}
      >
        <span className="flex flex-col gap-0.5 min-w-0 flex-1">
          <span className="text-body-sm text-paper-50 truncate">
            Stockfish ELO {game.level_elo} · {cadenceLabel} · {colorLabel}
          </span>
          <span className="text-caption text-ink-300">{dateLabel}</span>
        </span>
        <span className={`text-body-sm font-medium ${resultColor}`}>
          {resultLabel}
        </span>
      </button>
    </li>
  );
}
